export const NML_BOARDS = [
  { key: "SUZHOU", slug: "suzhou" },
  { key: "TURKEY", slug: "turkey" },
  { key: "GUANGZHOU", slug: "guangzhou" },
];

const NML_BOARD_BY_KEY = Object.fromEntries(NML_BOARDS.map((b) => [b.key, b]));

export function parseNmlSuzhouProductName(nombre) {
  const raw = String(nombre || "").trim();
  if (!raw) return null;
  const m = raw.match(/^(NML-\d+)\s*([A-Z]{2}\d[\w.]*)\s+(PO-\d+)\s*$/i);
  if (!m) return null;
  return {
    nmlNo: m[1].toUpperCase(),
    styleReference: m[2].toUpperCase(),
    nmlPo: m[3].toUpperCase(),
  };
}

export function normalizeNmlBoardKey(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const upper = raw.toUpperCase();
  if (upper === "WANGZHOU") return "GUANGZHOU";
  if (NML_BOARD_BY_KEY[upper]) return upper;
  const lower = raw.toLowerCase();
  if (lower === "suzhou") return "SUZHOU";
  if (lower === "turkey" || lower === "turquia" || lower === "turquía") {
    return "TURKEY";
  }
  if (lower === "guangzhou" || lower === "wangzhou") return "GUANGZHOU";
  return null;
}

export function nmlBoardFromSlug(slug) {
  const key = normalizeNmlBoardKey(slug);
  return key ? NML_BOARD_BY_KEY[key] : null;
}

export const NML_BOARD_EDIT_PERMISSION = {
  SUZHOU: "Permiso_NmlSuzhou",
  TURKEY: "Permiso_NmlTurkey",
  GUANGZHOU: "Permiso_NmlGuangzhou",
};

export function nmlBoardEditPermissionKey(boardKey) {
  const key = normalizeNmlBoardKey(boardKey);
  return key ? NML_BOARD_EDIT_PERMISSION[key] : null;
}

export function hasNmlFactoryPermission(permissions = {}) {
  return Boolean(
    permissions.Permiso_NmlSuzhou ||
    permissions.Permiso_NmlTurkey ||
    permissions.Permiso_NmlGuangzhou ||
    permissions.Permiso_NmlWangzhou,
  );
}

export function canAccessNmlSuzhou(permissions = {}) {
  return Boolean(
    hasNmlFactoryPermission(permissions) ||
    permissions.Permiso_NmlSuzhouCliente,
  );
}

export function canAccessNmlBoard(permissions = {}, boardKey) {
  const key = normalizeNmlBoardKey(boardKey);
  if (!key) return false;
  const permKey = nmlBoardEditPermissionKey(key);
  if (permKey && permissions[permKey]) return true;
  if (key === "GUANGZHOU" && Boolean(permissions.Permiso_NmlWangzhou)) {
    return true;
  }
  // NML CP (cliente) = vista cliente de Suzhou (no abre Turquía / Guangzhou solos).
  return (
    key === "SUZHOU" &&
    Boolean(permissions.Permiso_NmlSuzhouCliente) &&
    !hasNmlFactoryPermission(permissions)
  );
}

export function canEnrollNmlSuzhou(permissions = {}) {
  return hasNmlFactoryPermission(permissions);
}

export function canEnrollNmlBoard(permissions = {}, boardKey) {
  const permKey = nmlBoardEditPermissionKey(boardKey);
  if (permKey && permissions[permKey]) return true;
  const key = normalizeNmlBoardKey(boardKey);
  return key === "GUANGZHOU" && Boolean(permissions.Permiso_NmlWangzhou);
}

export function enrollableNmlBoards(permissions = {}) {
  return NML_BOARDS.filter((board) =>
    canEnrollNmlBoard(permissions, board.key),
  );
}

export function shouldPromptNmlEnroll(nombre) {
  const raw = String(nombre || "").trim();
  if (!raw) return false;
  if (parseNmlSuzhouProductName(raw)) return true;
  return /\bNML\b/i.test(raw) || /NML[-_]/i.test(raw);
}

export function isNmlSuzhouClientView(permissions = {}) {
  return resolveNmlClientView(permissions, false);
}

export function canToggleNmlClientView(permissions = {}) {
  return (
    Boolean(permissions.Permiso_NmlSuzhouCliente) &&
    hasNmlFactoryPermission(permissions)
  );
}

export function resolveNmlClientView(permissions = {}, requested = false) {
  if (!permissions.Permiso_NmlSuzhouCliente) return false;
  if (!hasNmlFactoryPermission(permissions)) return true;
  return Boolean(requested);
}

export function nmlSuzhouDateInputValue(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function formatNmlDateLabel(value) {
  const iso = nmlSuzhouDateInputValue(value);
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return `${day}-${MONTHS_SHORT[month - 1]}-${String(match[1]).slice(2)}`;
}

const MONTH_INDEX = Object.fromEntries(
  MONTHS_SHORT.map((label, index) => [label.toLowerCase(), index + 1]),
);

function pad2(n) {
  return String(n).padStart(2, "0");
}

/**
 * Normaliza texto pegado (ISO, 23-Sep-26, DD/MM/YYYY…) a `YYYY-MM-DD`.
 * `""` = borrar. `null` = no reconocido.
 */
export function parseNmlDatePaste(raw) {
  const text = String(raw ?? "")
    .trim()
    .replace(/\u00a0/g, " ");
  if (!text) return "";

  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  const labelMatch = text.match(
    /^(\d{1,2})[-\s/]([A-Za-z]{3})[-\s/](\d{2}|\d{4})$/,
  );
  if (labelMatch) {
    const day = Number(labelMatch[1]);
    const month = MONTH_INDEX[labelMatch[2].toLowerCase()];
    let year = Number(labelMatch[3]);
    if (year < 100) year += 2000;
    if (!month || day < 1 || day > 31) return null;
    return `${year}-${pad2(month)}-${pad2(day)}`;
  }

  const dmy = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    let year = Number(dmy[3]);
    if (year < 100) year += 2000;
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${year}-${pad2(month)}-${pad2(day)}`;
  }

  return null;
}
